<template>
  <v-card data-app>
    <v-card-title>
      Donations
      <v-spacer></v-spacer>
      <label class="switch">
        <input type="checkbox" v-model="isLiveMode" :disabled="modeSwitchLoading" checked />
        <span class="slider round"></span>
      </label>
      <span class="pr-10"> {{ isLiveMode ? "Live Mode" : "Test Mode" }} </span>
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Search"
        single-line
        hide-details
      ></v-text-field>
      <v-spacer></v-spacer>
      <b-form-select
        v-model="filterBy"
        placeholder="Filter By"
        :options="options"
        class="mb-3 reduce-screen"
      ></b-form-select>
      <v-col class="text-center" v-if="filterBy === 'Custom Date'">
        <div class="text-muted mb-2">Select From Date and To Date</div>
        <input type="date" v-model="start_date" />
        <input type="date" v-model="end_date" />
        <button type="button" class="filter-button" @click="fetchAllData" :disabled="reportLoading || !(start_date && end_date)">
          <span v-if="reportLoading">Loading...</span>
          <span v-else>Apply Filter</span>
        </button>
      </v-col>
      <v-col class="text-center" cols="12" sm="6" lg="1">
        <download-excel
          class="btn btn-primary"
          :data="exportData"
          :fields="json_fields"
          worksheet="My Worksheet"
          name="donations.xls"
        >
          Export
        </download-excel>
      </v-col>
    </v-card-title>
    <div v-if="listLoading || reportLoading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-3">Loading donations...</div>
    </div>
    <v-data-table
      v-show="!listLoading && !reportLoading"
      :items="tableData"
      :headers="headers"
      :search="search"
      :hide-default-footer="true"
      :items-per-page="-1"
    >
      <template #item.donation_id="{ value }">
        <router-link :to="`/donationView/${value}`"> {{ value }} </router-link>
      </template>
      <template #item.amount="{ value }">
        <span>{{ value | currency }} </span>
      </template>
    </v-data-table>
    <div class="button-center">
    <button
      class="filter-button"
      style="padding: 10px 20px 10px 20px"
      @click="fetchDonations(-1)"
      :disabled="reportMode ? reportPage <= 1 : listLoading"
    >
      Previous
    </button>
    <button
      class="filter-button"
      style="padding: 10px 20px 10px 20px"
      @click="fetchDonations(1)"
      :disabled="reportMode ? reportPage >= reportPageCount : listLoading"
    >
      Next
    </button>
    </div>
  </v-card>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { API_ENDPOINTS } from "@/core/services/api.endpoints";
import Vue from "vue";
import JsonExcel from "vue-json-excel";

Vue.component("downloadExcel", JsonExcel);
Vue.filter("currency", function (value) {
  var formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  });
  return formatter.format(value);
});

export default {
  name: "donations",
  data() {
    return {
      start_date: null,
      end_date: null,
      isLiveMode: true,
      filteredData: [],
      posts: [],
      errors: [],
      search: "",
      options: [
        "Filter By",
        "Today",
        "Yesterday",
        "This Week",
        "Last Week",
        "This Month",
        "Last Month",
        "This Year",
        "Custom Date",
        "All",
      ],
      filterBy: "Filter By",
      headers: [
        {
          text: "",
          align: "left",
          sortable: false,
          value: "name",
        },
        {
          text: "Id",
          value: "donation_id",
        },
        {
          text: "Project Name",
          value: "project_name",
        },
        {
          text: "Donor Name",
          value: "donor_name",
        },
        {
          text: "PAN",
          value: "pan_no",
        },
        {
          text: "Payment Date",
          value: "created_at",
        },
        {
          text: "Amount",
          value: "amount",
        },
        {
          text: "Status",
          value: "status",
        },
      ],

      json_fields: {
        Id: "donation_id",
        "Project Name": "project_name",
        "Payment Date": "created_at",
        Amount: "amount",
        Status: "status",
        "Referred By": "referred_by",
        "Donor Id": "donorId",
        "Donor Name": "donor_name",
        PAN: "pan_no",
        Phone: "phone",
        Email: "email",
        "Indian Passport Holder": "indian_passport_holder",
        Gender: "gender",
        DOB: "dob",
        Address: "address",
        City: "city",
        District: "district",
        State: "state",
        "Postal Code": "postal_code",
        Country: "country",
      },
      json_data: [],
      json_meta: [
        [
          {
            key: "charset",
            value: "utf-8",
          },
        ],
      ],
      paginationCount: 0,
      listLoading: false,
      reportLoading: false,
      modeSwitchLoading: false,
      reportMode: false,
      reportData: [],
      reportPage: 1,
      reportPageSize: 30,
    };
  },
  // Fetches posts when the component is created.
  created() {
    this.fetchDonations(0);
    this.isLiveMode = localStorage.getItem("isLiveMode") === "true";
  },
  watch: {
    filterBy() {
      if (this.filterBy !== "Custom Date") {
        this.fetchAllData();
      }
    },
    isLiveMode() {
      this.modeSwitchLoading = true;
      this.reportMode = false;
      localStorage.setItem("isLiveMode", this.isLiveMode);
      ApiService.setHeader();
      ApiService.post(API_ENDPOINTS.donations.modeSwitch(1), {
        isLivemode: this.isLiveMode ? "1" : "0",
      }).then(() => {
        this.fetchDonations(0);
      }).catch(() => {
        this.modeSwitchLoading = false;
      }).finally(() => {
        this.modeSwitchLoading = false;
      });
    },
  },
  methods: {
    fetchDonations(count) {
      if (this.reportMode) {
        const nextPage = this.reportPage + count;
        if (nextPage >= 1 && nextPage <= this.reportPageCount) {
          this.reportPage = nextPage;
        }
        return;
      }
      if (this.listLoading) {
        return;
      }
      this.listLoading = true;
      this.paginationCount += count;
      ApiService.setHeader();
      ApiService.query(API_ENDPOINTS.donations.list(0, this.paginationCount)).then(
        (response) => {
          // JSON responses are automatically parsed.
          this.posts = response.data;
          this.json_data = this.getDonationDetails(response);
          this.filteredData = [...this.json_data];
        }
      ).catch(() => {
        this.filteredData = [];
      }).finally(() => {
        this.listLoading = false;
      });
    },
    filterByDate(isCurrentDate) {
      let currentDate = new Date();
      if (!isCurrentDate) {
        currentDate.setDate(currentDate.getDate() - 1);
      }
      currentDate = currentDate.toISOString().slice(0, 10);
      this.filteredData = [];
      this.json_data.forEach((item) => {
        const date = item.created_at.slice(0, 10);
        if (date === currentDate) {
          this.filteredData.push(item);
        }
      });
    },
    filterByMonth(isCurrentMonth) {
      this.filteredData = [];
      const currentDate = new Date();
      let month = currentDate.getMonth();
      let year = currentDate.getFullYear();
      if (!isCurrentMonth) {
        month -= 1;
        if (month < 0) {
          month = 12;
          year -= 1;
        }
      }
      this.json_data.forEach((item) => {
        const itemMonth = new Date(item.created_at).getMonth();
        const itemYear = new Date(item.created_at).getFullYear();
        if (itemMonth === month && itemYear === year) {
          this.filteredData.push(item);
        }
      });
    },
    weekDiff(d1, d2) {
      let dif = Math.round(d1 - d2);
      return Math.ceil(dif / 1000 / 60 / 60 / 24 / 7);
    },
    getLastWeek() {
      let today = new Date();
      let lastWeek = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 7
      );
      return lastWeek;
    },
    filterByWeek(isCurrentWeek) {
      this.filteredData = [];
      var prevWeekDate = this.getLastWeek();
      this.json_data.forEach((item) => {
        if (isCurrentWeek) {
          if (this.weekDiff(new Date(item.created_at), prevWeekDate) > 1) {
            this.filteredData.push(item);
          }
        } else {
          if (this.weekDiff(new Date(item.created_at), prevWeekDate) == 1) {
            this.filteredData.push(item);
          }
        }
      });
    },
    filterByYear() {
      let currentDate = new Date().getFullYear();
      this.filteredData = [];
      this.json_data.forEach((item) => {
        let itemDate = new Date().getFullYear();
        if (itemDate === currentDate) {
          this.filteredData.push(item);
        }
      });
    },
    filterByCustomDate() {
      const startDate = new Date(this.start_date);
      const endDate = new Date(this.end_date);
      this.filteredData = [];
      this.json_data.forEach((item) => {
        const date = new Date(item.created_at.slice(0, 10));

        if (date >= startDate && date <= endDate) {
          this.filteredData.push(item);
        }
      });
    },
    getDonationDetails(response) {
      const payload = response && response.data;

      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload && Array.isArray(payload.donationDetails)) {
        return payload.donationDetails;
      }
      if (payload && payload.data) {
        if (Array.isArray(payload.data)) {
          return payload.data;
        }
        if (Array.isArray(payload.data.donationDetails)) {
          return payload.data.donationDetails;
        }
      }
      return [];
    },
    fetchAllData() {
      if (this.reportLoading) {
        return;
      }
      this.reportLoading = true;
      this.reportMode = true;
      ApiService.setHeader();
      ApiService.query(API_ENDPOINTS.reports.donationReports).then((response) => {
        // JSON responses are automatically parsed.
        this.posts = response.data;
        this.json_data = this.getDonationDetails(response);
        this.filteredData = [...this.json_data];
        if (this.filterBy === "Custom Date") {
          this.filterByCustomDate();
        }
        this.filterHandler();
        this.reportData = [...this.filteredData];
        this.reportPage = 1;
      }).catch(() => {
        this.reportData = [];
        this.reportPage = 1;
        this.filteredData = [];
      }).finally(() => {
        this.reportLoading = false;
      });
    },
    filterHandler() {
      switch (this.filterBy) {
        case "Today":
          this.filterByDate(true);
          break;
        case "Yesterday":
          this.filterByDate(false);
          break;
        case "Last Month":
          this.filterByMonth(false);
          break;
        case "This Month":
          this.filterByMonth(true);
          break;
        case "Last Week":
          this.filterByWeek(false);
          break;
        case "This Week":
          this.filterByWeek(true);
          break;
        case "This Year":
          this.filterByYear();
          break;
      }
    },
  },
  computed: {
    tableData() {
      if (!this.reportMode) {
        return this.filteredData;
      }
      const start = (this.reportPage - 1) * this.reportPageSize;
      return this.reportData.slice(start, start + this.reportPageSize);
    },
    exportData() {
      return this.reportMode ? this.reportData : this.filteredData;
    },
    reportPageCount() {
      return Math.max(1, Math.ceil(this.reportData.length / this.reportPageSize));
    },
  },
};
</script>

<style scoped>
/* Removes the clear button from date inputs */
input[type="date"]::-webkit-clear-button {
  display: none;
}

/* Removes the spin button */
input[type="date"]::-webkit-inner-spin-button {
  display: none;
}

/* Always display the drop down caret */
input[type="date"]::-webkit-calendar-picker-indicator {
  color: #2c3e50;
}

/* A few custom styles for date inputs */
input[type="date"] {
  appearance: none;
  margin-right: 20px;
  -webkit-appearance: none;
  color: #95a5a6;
  font-family: "Helvetica", arial, sans-serif;
  font-size: 18px;
  border: 1px solid #ecf0f1;
  background: #ecf0f1;
  padding: 2px;
  display: inline-block !important;
  visibility: visible !important;
}

input[type="date"],
focus {
  color: #95a5a6;
  box-shadow: none;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
}

.filter-button {
  font-size: 14px;
  background: #ecf0f1;
  padding: 5px;
  margin: 10px;
}

.filter-button:disabled {
  pointer-events: none;
}

.button-center{
  text-align: center;
}

.reduce-screen{
width: 25%;
}
</style>
